import FinancialService from '../../../AppSettings/services/FinancialService';
import Select from 'react-select';
import styles from './AddDisbursement.module.css';
import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import AgentsService from '@frontend/AppSettings/services/AgentsService';

const AddDisbursement = ({ onClose, onAddSuccess }) => {
  const { matterId } = useParams();
  const [error, setError] = useState('');
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState(null);
  const [fixedDisbursements, setFixedDisbursements] = useState([]);
  const [loadingFixedDisbursements, setLoadingFixedDisbursements] =
    useState(true);
  const [isAddingFixed, setIsAddingFixed] = useState(false);

  const [newDisbursementData, setNewDisbursementData] = useState({
    agentid: '',
    description: '',
    fees: '',
    date_added: null,
  });

  const [newFixedDisbursementData, setNewFixedDisbursementData] = useState({
    description: '',
    fees: '',
  });

  const modalRef = useRef(null);

  // Fetch fixed disbursements
  useEffect(() => {
    const fetchFixedDisbursements = async () => {
      try {
        setLoadingFixedDisbursements(true);
        const data = await FinancialService.getFixedDisbursements();
        setFixedDisbursements(
          data.map((disbursement) => ({
            value: disbursement.id,
            label: `${disbursement.description} ($${disbursement.fees})`,
            description: disbursement.description,
            fees: disbursement.fees,
          }))
        );
      } catch (err) {
        setError('Error fetching fixed disbursements');
      } finally {
        setLoadingFixedDisbursements(false);
      }
    };

    fetchFixedDisbursements();
  }, []);

  // Fetch agents
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setAgentsLoading(true);
        const data = await AgentsService.getAgents();
        const sortedAgents = data.sort((a, b) =>
          a.AgentID.localeCompare(b.AgentID)
        );
        setAgents(
          sortedAgents.map((agent) => ({
            value: agent.AgentID,
            label: `${agent.AgentID}: ${agent.FirstName} ${agent.LastName}`,
          }))
        );
      } catch (err) {
        setAgentsError('Error fetching agents');
        console.error('Error fetching agents:', err);
      } finally {
        setAgentsLoading(false);
      }
    };

    fetchAgents();
  }, []);

  const handleSelectChange = (selectedOption) => {
    setNewDisbursementData({
      ...newDisbursementData,
      agentid: selectedOption ? selectedOption.value : '',
    });
  };

  const handleFixedDisbursementChange = (selectedOption) => {
    if (selectedOption) {
      setNewDisbursementData({
        ...newDisbursementData,
        description: selectedOption.description,
        fees: selectedOption.fees,
      });
    }
  };

  const handleNewDisbursementSubmit = async (e) => {
    e.preventDefault();
    try {
      await FinancialService.addAgentDisbursement({
        ...newDisbursementData,
        matterid: matterId,
      });
      onAddSuccess();
      setNewDisbursementData({
        agentid: '',
        description: '',
        fees: '',
        date_added: null,
      });
      onClose();
    } catch (err) {
      setError('Failed to add disbursement.');
      console.error('Error submitting new disbursement:', err);
    }
  };

  const handleNewFixedDisbursementSubmit = async (e) => {
    e.preventDefault();
    try {
      await FinancialService.addFixedDisbursement(newFixedDisbursementData);
      // Refresh the fixed disbursements list
      const data = await FinancialService.getFixedDisbursements();
      setFixedDisbursements(
        data.map((disbursement) => ({
          value: disbursement.id,
          label: `${disbursement.description} ($${disbursement.fees})`,
          description: disbursement.description,
          fees: disbursement.fees,
        }))
      );
      setNewFixedDisbursementData({
        description: '',
        fees: '',
      });
      setIsAddingFixed(false);
      setError('');
    } catch (err) {
      setError('Failed to add fixed disbursement.');
    }
  };

  const handlePositiveNumberInput = (e) => {
    const value = e.target.value;
    setNewDisbursementData({
      ...newDisbursementData,
      fees: value && Math.abs(value) >= 0 ? Math.abs(value) : '',
    });
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  if (agentsLoading || loadingFixedDisbursements) {
    return <div>Loading...</div>;
  }

  if (error || agentsError) {
    return <div>{error || agentsError}</div>;
  }

  const selectedAgent = agents.find(
    (option) => option.value === newDisbursementData.agentid
  );

  return (
    <div className={styles['matterDetailsDisbursements-overlay']}>
      <div
        className={styles['matterDetailsDisbursements-modal']}
        ref={modalRef}
      >
        <div
          className={
            styles['matterDetailsDisbursements-title-button-container']
          }
        >
          <h2>
            {isAddingFixed
              ? 'Add Fixed Disbursement'
              : 'Add Matter Disbursement'}
          </h2>
          <button
            type="button"
            className={styles['matterDetailsDisbursements-toggle-form-button']}
            onClick={() => setIsAddingFixed(!isAddingFixed)}
          >
            Switch to {isAddingFixed ? 'Regular' : 'Fixed'} Disbursement
          </button>
        </div>

        {isAddingFixed ? (
          <form
            className={styles['matterDetailsDisbursements-new-fee-form']}
            onSubmit={handleNewFixedDisbursementSubmit}
          >
            <input
              type="text"
              placeholder="Description"
              value={newFixedDisbursementData.description}
              onChange={(e) =>
                setNewFixedDisbursementData({
                  ...newFixedDisbursementData,
                  description: e.target.value,
                })
              }
              required
            />
            <input
              type="number"
              min="0"
              placeholder="Fees"
              value={newFixedDisbursementData.fees}
              onChange={(e) =>
                setNewFixedDisbursementData({
                  ...newFixedDisbursementData,
                  fees: Math.abs(e.target.value),
                })
              }
              required
            />
            <button
              type="submit"
              className={styles['matterDetailsDisbursements-submit-fee-button']}
            >
              Add Fixed Disbursement
            </button>
          </form>
        ) : (
          <form
            className={styles['matterDetailsDisbursements-new-fee-form']}
            onSubmit={handleNewDisbursementSubmit}
          >
            <div className={styles['matterDetailsDisbursements-form-group']}>
              <label
                className={
                  styles['matterDetailsDisbursements-form-group-label']
                }
              >
                Agent
              </label>
              <Select
                options={agents}
                value={selectedAgent || null}
                onChange={handleSelectChange}
                placeholder="Select or search an agent..."
                isClearable
                isLoading={agentsLoading}
              />
            </div>

            <div className={styles['matterDetailsDisbursements-form-group']}>
              <label
                className={
                  styles['matterDetailsDisbursements-form-group-label']
                }
              >
                Fixed Disbursement (Optional)
              </label>
              <Select
                options={fixedDisbursements}
                onChange={handleFixedDisbursementChange}
                isLoading={loadingFixedDisbursements}
                placeholder="Select a fixed disbursement..."
                isClearable
              />
            </div>

            <input
              type="text"
              placeholder="Description"
              value={newDisbursementData.description}
              onChange={(e) =>
                setNewDisbursementData({
                  ...newDisbursementData,
                  description: e.target.value,
                })
              }
              required
            />
            <input
              type="number"
              min="0"
              onInput={handlePositiveNumberInput}
              placeholder="Fees"
              value={newDisbursementData.fees}
              required
            />
            <input
              type="date"
              placeholder="Date Added (Optional)"
              value={newDisbursementData.date_added || ''}
              onChange={(e) =>
                setNewDisbursementData({
                  ...newDisbursementData,
                  date_added: e.target.value || null,
                })
              }
            />
            <button
              type="submit"
              className={styles['matterDetailsDisbursements-submit-fee-button']}
            >
              Submit Matter Disbursement
            </button>
          </form>
        )}

        {error && (
          <div className={styles['matterDetailsDisbursements-error-message']}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddDisbursement;
